// src/components/DocumentForgery/DocumentUpload.jsx
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadCloud, MoreVertical, FileText, AlertCircle, ArrowRight, Plus } from 'react-feather';
import PdfThumbnail from './PdfThumbnail';
import DocumentCard from './history/DocumentCard';
import ScanIcon from '../../assets/icons/research (1).gif';
import { uploadDocsHandler, getDocumentListHandler } from '../../services/documentForgery';
import DetectionSettings from './DetectionSettings';
import CustomFieldsPopup from './CustomFieldsPopup';
import { showSuccessToast, showErrorToast } from '../../utility/helper';
import pusherManager from '../../utility/pusherManager';

const PIPELINE_STEPS = [
  { key: 'document_uploaded',  label: 'Uploaded'   },
  { key: 'ocr_complete',       label: 'OCR'        },
  { key: 'features_extracted', label: 'Fields'     },
  { key: 'issues_identified',  label: 'Issues'     },
  { key: 'analysis_complete',  label: 'Complete'   },
];
const TOTAL_STEPS = PIPELINE_STEPS.length;

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Step progress indicator ────────────────────────────────────────────────
const StepIndicator = ({ currentStep, message, failed, failedMessage }) => {
  const completedIdx = PIPELINE_STEPS.findIndex(s => s.key === currentStep);
  const pct = Math.max(((completedIdx + 1) / TOTAL_STEPS) * 100, 4);

  return (
    <div style={{
      width: '100%', marginTop: 20,
      padding: '18px 20px',
      background: failed ? '#fff5f5' : '#f8faff',
      borderRadius: 10,
      border: `1px solid ${failed ? '#fecaca' : '#dbeafe'}`,
    }}>
      {/* Circles + connectors */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        {PIPELINE_STEPS.map((step, idx) => {
          const done   = completedIdx >= idx;
          const active = completedIdx === idx;
          const last   = idx === PIPELINE_STEPS.length - 1;
          const color  = failed && active ? '#ef4444' : done ? '#44ace4' : '#d1d5db';
          return (
            <div key={step.key} style={{ display: 'flex', alignItems: 'center', flex: last ? '0 0 auto' : 1 }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                background: done ? color : '#fff',
                border: `2px solid ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
                boxShadow: active ? `0 0 0 3px ${color}33` : 'none',
                transition: 'all 0.3s',
              }}>
                <span style={{ color: done ? '#fff' : color }}>
                  {failed && active ? '✕' : done ? '✓' : idx + 1}
                </span>
              </div>
              {!last && (
                <div style={{
                  flex: 1, height: 2, margin: '0 2px',
                  background: completedIdx > idx ? '#44ace4' : '#e5e7eb',
                  transition: 'background 0.3s',
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        {PIPELINE_STEPS.map((step, idx) => {
          const done   = completedIdx >= idx;
          const active = completedIdx === idx;
          return (
            <div key={step.key} style={{
              flex: 1, textAlign: 'center',
              fontSize: 9, fontWeight: active ? 700 : 500,
              color: done ? (failed && active ? '#ef4444' : '#44ace4') : '#9ca3af',
              letterSpacing: '0.3px',
            }}>
              {step.label}
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div style={{ height: 5, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: failed ? '#ef4444' : 'linear-gradient(90deg, #44ace4, #6366f1)',
          borderRadius: 99, transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>

      {/* Message */}
      <p style={{ margin: 0, fontSize: 11, textAlign: 'center', fontWeight: 500, color: failed ? '#dc2626' : '#374151' }}>
        {failed ? (failedMessage || 'Analysis failed. Please try again.') : (message || 'Starting…')}
      </p>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const DocumentUpload = () => {
  const [droppedFiles,  setDroppedFiles]  = useState([]);
  const [loader,        setLoader]        = useState(false);
  const [menuOpen,      setMenuOpen]      = useState(null);
  const [currentStep,   setCurrentStep]   = useState(null);
  const [stepMessage,   setStepMessage]   = useState('');
  const [socketFailed,  setSocketFailed]  = useState(false);
  const [failedMessage, setFailedMessage] = useState('');
  const [recentDocs,    setRecentDocs]    = useState([]);

  const [customFieldsOpen, setCustomFieldsOpen] = useState(false);
  const fileInputRef = useRef(null);
  const unsubRef     = useRef(null);
  const navigate     = useNavigate();

  // Fetch recent documents on mount
  useEffect(() => {
    getDocumentListHandler({ page_size: 5, ordering: '-created_at' })
      .then(res => {
        const items = res?.data?.results || res?.data || [];
        setRecentDocs(Array.isArray(items) ? items.slice(0, 5) : []);
      })
      .catch(() => {}); // silent — recent docs are non-critical
  }, []);

  useEffect(() => () => unsubRef.current?.(), []);

  const handleFiles = (files) => {
    const list = Array.from(files);
    setDroppedFiles(list);
    if (list.length > 0) handleDocSubmit(list[0]);
  };

  const handleDrop            = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };
  const handleDragOver        = (e) => e.preventDefault();
  const handleClick           = () => fileInputRef.current.click();
  const handleFileInputChange = (e) => handleFiles(e.target.files);
  const handleDelete          = (f) => setDroppedFiles(prev => prev.filter(x => x !== f));

  const handleDocSubmit = useCallback((file) => {
    if (!file) return;
    setLoader(true);
    setCurrentStep(null);
    setStepMessage('Uploading your document…');
    setSocketFailed(false);
    setFailedMessage('');

    const formData = new FormData();
    formData.append('document',   file);
    formData.append('doc_type',   'INVOICE');
    formData.append('doc_format', file.type);
    formData.append('llm_model',  'gpt-4.1');
    formData.append('ocr_model',  'azure_ocr');

    uploadDocsHandler(formData, true)
      .then(res => {
        const { id, socket_id, detail_url } = res.data;
        setCurrentStep('document_uploaded');
        setStepMessage('Document saved. Connecting to analysis pipeline…');

        const unsub = pusherManager.subscribeAnalysisPipeline(socket_id, {
          onStep: (data) => {
            setCurrentStep(data.step);
            setStepMessage(data.message || data.step_label);
          },
          onComplete: (data) => {
            setLoader(false);
            setCurrentStep('analysis_complete');
            setStepMessage('Analysis complete! Redirecting…');
            showSuccessToast('Analysis complete');
            setTimeout(() => navigate(`/dashboard/analysis/${id}`, { state: { detail_url } }), 800);
          },
          onFailed: (data) => {
            setLoader(false);
            setSocketFailed(true);
            setFailedMessage(data.error || 'Analysis failed. Please try again.');
            showErrorToast(data.error || 'Analysis failed');
          },
        });
        unsubRef.current = unsub;
      })
      .catch(err => {
        setLoader(false);
        setSocketFailed(true);
        setFailedMessage('Upload failed. Please check your connection and try again.');
        showErrorToast('Failed to upload document');
      });
  }, [navigate]);

  return (
    <div className="document-area">
      {customFieldsOpen && (
        <CustomFieldsPopup onClose={() => setCustomFieldsOpen(false)} />
      )}

      {/* ── Left panel ── */}
      <div className="document-area__inner">

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ color: 'black', marginBottom: 0, fontWeight: 600 }}>Documents</h3>
            <p className="paragraph">Upload and manage documents for analysis</p>
          </div>
          <button
            onClick={() => setCustomFieldsOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8,
              background: '#f0f7ff', border: '1px solid #d8eef8',
              color: '#44ace4', fontWeight: 600, fontSize: 12,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <Plus size={13} />
            Custom Fields
          </button>
        </div>

        <div className="notification-panel" style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <h4 style={{ margin: 0 }}>Add document to get started</h4>
            <DetectionSettings docType="INVOICE" />
          </div>
          <span className="notification-panel__discription">
            Upload a document and instantly see the analysis.
          </span>
        </div>

        {/* Dropzone */}
        {droppedFiles.length === 0 && (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={handleClick}
            style={{
              border: '2px dashed #ccd6e0', borderRadius: 8, padding: '32px 24px',
              textAlign: 'center', color: '#555', cursor: 'pointer',
              marginTop: 20, background: '#f8fbff',
              transition: 'border-color 0.2s, background 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#44ace4'; e.currentTarget.style.background = '#f0f7ff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#ccd6e0'; e.currentTarget.style.background = '#f8fbff'; }}
          >
            <UploadCloud size={44} color="#44ace4" style={{ marginBottom: 12, opacity: 0.8 }} />
            <p style={{ fontWeight: 600, marginBottom: 4, fontSize: 14 }}>Drag & drop files here, or click to browse</p>
            <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Supported: PDF, PNG, JPG, WEBP (max 20 MB)</p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.tiff"
            />
          </div>
        )}

        {/* Dropped file card */}
        <div className="document-list">
          {droppedFiles.map((file, index) => (
            <div className="document-card" key={index}>
              <div className="doc-left">
                {file.type === 'application/pdf'
                  ? <PdfThumbnail file={file} />
                  : file.type.startsWith('image/')
                    ? <img src={URL.createObjectURL(file)} alt={file.name}
                        style={{ width: 52, height: 64, objectFit: 'cover', borderRadius: 5, border: '1px solid #e5e7eb' }}
                      />
                    : <div style={{ width: 52, height: 64, background: '#f0f7ff', borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileText size={20} color="#44ace4" />
                      </div>
                }
                <div className="doc-info">
                  <span className="doc-name">{file.name}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatBytes(file.size)}</span>
                </div>
              </div>
              <div className="doc-right">
                <div style={{ position: 'relative' }}>
                  <MoreVertical size={16}
                    onClick={() => setMenuOpen(menuOpen === index ? null : index)}
                    style={{ cursor: 'pointer', color: '#bbb' }}
                  />
                  {menuOpen === index && (
                    <div style={{
                      position: 'absolute', top: 24, right: 0,
                      background: '#fff', border: '1px solid #ddd',
                      borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10,
                    }}>
                      <div style={{ padding: '9px 14px', cursor: 'pointer', color: '#e74c3c', fontSize: 13, fontWeight: 500 }}
                        onClick={() => { handleDelete(file); setMenuOpen(null); }}>
                        Delete
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Recent documents ── */}
        {recentDocs.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>Recent Documents</h5>
              <button
                onClick={() => navigate('/history')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 12, fontWeight: 600, color: '#44ace4',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}
              >
                View all <ArrowRight size={13} />
              </button>
            </div>
            <div className="docs-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
              {recentDocs.map(doc => (
                <DocumentCard data={doc} key={doc.id} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Right preview panel ── */}
      <div className="document-preview">
        <h2>InsightAI Fraud Analyst</h2>

        <div className="document-preview__analysis">
          {loader || currentStep ? (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Scan GIF — always shown while processing */}
              <img
                src={ScanIcon}
                alt="Scanning"
                style={{ width: 90, height: 90, objectFit: 'contain', marginBottom: 4 }}
              />

              {/* Step indicator */}
              {currentStep && (
                <StepIndicator
                  currentStep={currentStep}
                  message={stepMessage}
                  failed={socketFailed}
                  failedMessage={failedMessage}
                />
              )}
              {!currentStep && loader && (
                <p style={{ marginTop: 10, color: 'black', fontWeight: 700, fontSize: 13 }}>
                  Uploading your document…
                </p>
              )}

              {/* Retry on failure */}
              {socketFailed && droppedFiles.length > 0 && (
                <button
                  onClick={() => handleDocSubmit(droppedFiles[0])}
                  style={{
                    marginTop: 14, padding: '8px 18px',
                    background: '#44ace4', color: '#fff',
                    border: 'none', borderRadius: 8,
                    fontWeight: 600, cursor: 'pointer', fontSize: 12,
                  }}
                >
                  Retry Analysis
                </button>
              )}
            </div>
          ) : droppedFiles.length > 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280' }}>
              <AlertCircle size={26} style={{ marginBottom: 8 }} />
              <p style={{ fontWeight: 600, color: '#111', margin: 0 }}>Ready to analyse</p>
            </div>
          ) : (
            <>
              <UploadCloud size={28} color="#d1d5db" />
              <p style={{ color: 'black', fontWeight: 800, marginBottom: 0, marginTop: 18, fontSize: 14 }}>Add document to get started</p>
              <p style={{ color: '#9ca3af', fontSize: 12 }}>Analysis will appear here</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;